import httpClient from "../httpClient";

// ===== INTERFACES =====

export interface UploadStats {
  totalRows: number;
  validRows: number;
  errorRows: number;
  warnings: string[];
  errors: ValidationError[];
  inserted?: number;
  updated?: number;
  skipped: number;
  emailsSent?: number;
  emailsFailed?: number;
}

export interface ValidationError {
  row: number;
  field: string;
  message: string;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  data: UploadStats;
}

// ===== API ENDPOINTS =====

const ENDPOINTS = {
  UPLOAD_USERS: "/super-admin/bulk-upload/users",
  UPLOAD_CONTENT: "/super-admin/bulk-upload/content",
  UPLOAD_ASSIGNMENTS: "/super-admin/bulk-upload/assignments",
  DOWNLOAD_TEMPLATE: "/super-admin/bulk-upload/template",
  DOWNLOAD_SAMPLE: "/super-admin/bulk-upload/sample",
};

/**
 * Upload users CSV file
 * @param file - CSV file containing user data
 * @param organizationId - ID of the organization to assign users to
 * @returns Upload response with statistics
 */
export const uploadUsers = async (file: File, organizationId: string): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("organizationId", organizationId);

    const response = await httpClient.post<UploadResponse>(
      ENDPOINTS.UPLOAD_USERS,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to upload users"
    );
  }
};

/**
 * Upload content updates CSV file
 * @param file - CSV file containing content updates
 * @returns Upload response with statistics
 */
export const uploadContent = async (file: File): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await httpClient.post<UploadResponse>(
      ENDPOINTS.UPLOAD_CONTENT,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to upload content updates"
    );
  }
};

/**
 * Upload course assignments CSV file
 * @param file - CSV file containing course assignments
 * @returns Upload response with statistics
 */
export const uploadAssignments = async (
  file: File
): Promise<UploadResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await httpClient.post<UploadResponse>(
      ENDPOINTS.UPLOAD_ASSIGNMENTS,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to upload assignments"
    );
  }
};

/**
 * Download CSV template for a specific upload type
 * @param type - Template type ('users', 'content', 'assignments')
 */
export const downloadTemplate = async (
  type: "users" | "content" | "assignments"
): Promise<void> => {
  try {
    const response = await httpClient.get(
      `${ENDPOINTS.DOWNLOAD_TEMPLATE}/${type}`,
      {
        responseType: "blob",
      } as any
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${type}_template.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to download template"
    );
  }
};

/**
 * Download sample CSV file with example data
 * @param type - Sample type ('users', 'content', 'assignments')
 */
export const downloadSample = async (
  type: "users" | "content" | "assignments"
): Promise<void> => {
  try {
    const response = await httpClient.get(
      `${ENDPOINTS.DOWNLOAD_SAMPLE}/${type}`,
      {
        responseType: "blob",
      } as any
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${type}_sample.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to download sample"
    );
  }
};
